import React, { useState, useEffect, useMemo } from 'react';
import { placeLabel } from '../lib/placeLabel';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { Calendar, Clock, MapPin, Users, Luggage, Phone, Mail, CheckCircle2, XCircle, Send, Car, Edit, Trash2, Plane, ExternalLink, Search, FileSearch, FileText, X, CalendarPlus, RefreshCw } from 'lucide-react';
import { formatDisplay } from '../lib/phone';
import { formatUkDate, formatUkDateTime } from '../lib/date';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import JobLogTimeline from '../components/JobLogTimeline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Build a Google Calendar "add event" template link, pre-filled from the booking.
// Works for any admin instantly (no OAuth needed) — opens their personal Google Calendar.
const buildGoogleCalendarUrl = (b) => {
  try {
    const [y, mo, d] = String(b.pickupDate).split('-').map((n) => parseInt(n, 10));
    const [h, mi] = String(b.pickupTime).split(':').map((n) => parseInt(n, 10));
    const start = new Date(Date.UTC(y, mo - 1, d, h, mi || 0));
    if (isNaN(start.getTime())) return null;
    const hours = parseFloat(b.asDirectedHours) || 2;
    const end = new Date(start.getTime() + hours * 3600 * 1000);
    const fmt = (dt) => dt.toISOString().replace(/[-:]/g, '').split('.')[0];
    const dates = `${fmt(start)}/${fmt(end)}`;
    const title = `Obsidian Chauffeurs - ${b.serviceType || 'Booking'}`;
    const detailLines = [
      `Booking Ref: ${b.id || ''}`,
      b.vehicleName ? `Vehicle: ${b.vehicleName}` : '',
      b.dropoffLocation ? `Drop-off: ${b.dropoffLocation}` : '',
      `Passengers: ${b.passengers || ''} | Luggage: ${b.luggage || ''}`,
      b.flightNumber ? `Flight: ${b.flightNumber}` : '',
      b.specialRequests ? `Notes: ${b.specialRequests}` : '',
    ].filter(Boolean);
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates,
      details: detailLines.join('\n'),
      location: b.pickupLocation || '',
      ctz: 'Europe/London',
    });
    return `https://www.google.com/calendar/render?${params.toString()}`;
  } catch {
    return null;
  }
};
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const GMAPS_LIBRARIES = ['places'];
const AS_DIRECTED_HOURS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 12];

const AdminDashboard = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [driverFilter, setDriverFilter] = useState('all');
  const [roundTripsOnly, setRoundTripsOnly] = useState(false);
  const [legFareEdits, setLegFareEdits] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteDistance, setQuoteDistance] = useState('');
  const [quoteEstimatedTime, setQuoteEstimatedTime] = useState('');
  const [outboundFare, setOutboundFare] = useState('');
  const [returnFare, setReturnFare] = useState('');
  const [sendingQuote, setSendingQuote] = useState(false);
  
  // New states for edit functionality
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editPickupAutocomplete, setEditPickupAutocomplete] = useState(null);
  const [editDropoffAutocomplete, setEditDropoffAutocomplete] = useState(null);
  const [editDistance, setEditDistance] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editCalculating, setEditCalculating] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showJobLog, setShowJobLog] = useState(false);

  const { isLoaded: gmapsLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GMAPS_LIBRARIES,
  });

  // Free-text search + driver filter applied client-side on the loaded bookings.
  const filteredBookings = useMemo(() => {
    let arr = bookings;
    if (driverFilter !== 'all') {
      arr = arr.filter((b) =>
        driverFilter === 'unassigned'
          ? !(b.assignedDriverId || b.assignedDriver)
          : (b.assignedDriverId || b.assignedDriver) === driverFilter
      );
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      arr = arr.filter((b) => {
        const hay = [
          b.id,
          b.firstName,
          b.lastName,
          `${b.firstName || ''} ${b.lastName || ''}`,
          b.email,
          b.phone,
          b.pickupLocation,
          b.dropoffLocation,
          b.pickupDate,
          b.pickupTime,
          b.vehicleName,
          b.companyName,
          b.flightNumber,
          b.passengerName,
          b.passengerEmail,
          b.passengerPhone,
          ...(b.stopovers || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    // Round Trips: show only paired legs, each outbound immediately followed by
    // its return leg so dispatchers see the pair grouped together.
    if (roundTripsOnly) {
      const paired = arr.filter((b) => b.linkedBookingId);
      const byId = Object.fromEntries(paired.map((b) => [b.id, b]));
      const ordered = [];
      const seen = new Set();
      paired.forEach((b) => {
        if (b.legType === 'return') return;
        ordered.push(b);
        seen.add(b.id);
        const ret = byId[b.linkedBookingId];
        if (ret && !seen.has(ret.id)) {
          ordered.push(ret);
          seen.add(ret.id);
        }
      });
      paired.forEach((b) => {
        if (!seen.has(b.id)) ordered.push(b);
      });
      return ordered;
    }
    return arr;
  }, [bookings, searchQuery, driverFilter, roundTripsOnly]);

  // KPI counts derived from the full bookings list (not the filtered view)
  // so the stats remain consistent as the user filters.
  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      total: bookings.length,
      todays: bookings.filter((b) => b.pickupDate === today && b.status !== 'cancelled').length,
      pendingQuotes: bookings.filter((b) =>
        b.status === 'quote_requested' || b.status === 'modification_pending'
      ).length,
      inProgress: bookings.filter((b) =>
        ['on_the_way', 'arrived', 'pob'].includes(b.driverStatus) ||
        b.status === 'in_progress'
      ).length,
      confirmed: bookings.filter((b) => b.status === 'confirmed').length,
      completed: bookings.filter((b) => b.status === 'completed').length,
    };
  }, [bookings]);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load drivers for the driver filter dropdown (once on mount).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/drivers`);
        if (active) setDrivers(data.drivers || []);
      } catch (e) {
        // non-blocking — filter just stays empty
      }
    })();
    return () => { active = false; };
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const url = statusFilter === 'all' 
        ? `${API}/bookings?limit=100`
        : `${API}/bookings?limit=100&status=${statusFilter}`;
      
      const response = await axios.get(url);
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const saveLegFare = async (bookingId, value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) {
      toast.error('Enter a valid fare');
      return;
    }
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/leg-fare`, { legFare: num });
      toast.success('Return fare saved & invoice repriced');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save fare');
    }
  };

  const handleSendQuote = async () => {
    const isReturnQuote = selectedBooking?.returnRequired === 'yes';
    let amount;
    const extra = {};
    if (isReturnQuote) {
      const o = parseFloat(outboundFare) || 0;
      const r = parseFloat(returnFare) || 0;
      if (o <= 0 || r <= 0) {
        toast.error('Enter both the outbound and return fares');
        return;
      }
      amount = Math.round((o + r) * 100) / 100;
      extra.outboundFare = o;
      extra.returnFare = r;
    } else {
      if (!quoteAmount || parseFloat(quoteAmount) <= 0) {
        toast.error('Please enter a valid quote amount');
        return;
      }
      amount = parseFloat(quoteAmount);
    }

    try {
      setSendingQuote(true);
      const isReQuote = selectedBooking?.status === 'modification_pending';
      const endpoint = isReQuote
        ? `${API}/admin/bookings/${selectedBooking.id}/approve-modification`
        : `${API}/admin/bookings/${selectedBooking.id}/send-quote`;

      await axios.post(endpoint, {
        amount,
        notes: quoteNotes,
        distance: quoteDistance ? parseFloat(quoteDistance) : null,
        estimatedTime: quoteEstimatedTime || null,
        ...extra,
      });

      toast.success(isReQuote ? 'Updated quote sent — awaiting customer re-acceptance' : 'Quote sent successfully!');
      setShowQuoteModal(false);
      setQuoteAmount('');
      setQuoteNotes('');
      setQuoteDistance('');
      setQuoteEstimatedTime('');
      setOutboundFare('');
      setReturnFare('');
      setSelectedBooking(null);
      fetchBookings();
    } catch (error) {
      console.error('Error sending quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to send quote');
    } finally {
      setSendingQuote(false);
    }
  };

  const openQuoteModal = (booking) => {
    setSelectedBooking(booking);
    // Pre-fill with calculated values if available
    setQuoteDistance(booking.calculatedDistance || '');
    setQuoteEstimatedTime(booking.calculatedTime || '');
    // Return-journey per-leg fares
    setOutboundFare(booking.outboundFare != null ? String(booking.outboundFare) : '');
    setReturnFare(booking.returnFare != null ? String(booking.returnFare) : '');
    // For a re-quote, seed with the previous price so the admin can tweak it
    if (booking.status === 'modification_pending') {
      setQuoteAmount(booking.quoteAmount ? String(booking.quoteAmount) : '');
      setQuoteNotes(booking.quoteNotes || '');
    } else {
      setQuoteAmount('');
      setQuoteNotes('');
    }
    setShowQuoteModal(true);
  };

  const isAsDirectedBooking = (booking) => {
    return (booking?.serviceType || '').toLowerCase().includes('as directed');
  };

  const parseAsDirectedHours = (dropoffLocation, fallback) => {
    // Existing format: "As Directed - 4 hours (60 miles allowance)"
    const match = (dropoffLocation || '').match(/(\d+)\s*hours?/i);
    return match ? match[1] : (fallback || '4');
  };

  const openEditModal = (booking) => {
    setSelectedBooking(booking);
    const asDirected = isAsDirectedBooking(booking);
    setEditFormData({
      isAsDirected: asDirected,
      pickupLocation: booking.pickupLocation || '',
      dropoffLocation: asDirected ? '' : (booking.dropoffLocation || ''),
      hours: asDirected ? parseAsDirectedHours(booking.dropoffLocation, booking.hours) : (booking.hours || ''),
      pickupDate: booking.pickupDate || '',
      pickupTime: booking.pickupTime || '',
      passengers: booking.passengers || '',
      luggage: booking.luggage || '',
      flightNumber: booking.flightNumber || '',
      specialRequests: booking.specialRequests || '',
      hideCustomerPhone: !!booking.hideCustomerPhone,
      quoteAmount: booking.quoteAmount != null ? String(booking.quoteAmount) : '',
      quoteNotes: booking.quoteNotes || ''
    });
    setEditDistance(booking.calculatedDistance || '');
    setEditTime(booking.calculatedTime || '');
    setShowEditModal(true);
  };

  // Auto-calculate distance/time inside edit modal when pickup/dropoff change (Transfer only)
  useEffect(() => {
    if (!showEditModal || !gmapsLoaded || editFormData.isAsDirected) return;
    if (!editFormData.pickupLocation || !editFormData.dropoffLocation) return;
    if (!window.google || !window.google.maps) return;

    setEditCalculating(true);
    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [editFormData.pickupLocation],
        destinations: [editFormData.dropoffLocation],
        travelMode: 'DRIVING',
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      },
      (response, status) => {
        if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
          const result = response.rows[0].elements[0];
          const miles = (result.distance.value / 1609.34).toFixed(1);
          const mins = result.duration.value / 60;
          const h = Math.floor(mins / 60);
          const m = Math.round(mins % 60);
          setEditDistance(miles);
          setEditTime(
            h > 0
              ? `${h} hour${h > 1 ? 's' : ''} ${m} minute${m !== 1 ? 's' : ''}`
              : `${m} minute${m !== 1 ? 's' : ''}`
          );
        }
        setEditCalculating(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFormData.pickupLocation, editFormData.dropoffLocation, gmapsLoaded, showEditModal, editFormData.isAsDirected]);

  const onEditPickupPlaceChanged = () => {
    if (editPickupAutocomplete) {
      const place = editPickupAutocomplete.getPlace();
      setEditFormData((prev) => ({ ...prev, pickupLocation: placeLabel(place, prev.pickupLocation) }));
    }
  };

  const onEditDropoffPlaceChanged = () => {
    if (editDropoffAutocomplete) {
      const place = editDropoffAutocomplete.getPlace();
      setEditFormData((prev) => ({ ...prev, dropoffLocation: placeLabel(place, prev.dropoffLocation) }));
    }
  };

  const handleEditBooking = async () => {
    try {
      const payload = {
        pickupLocation: editFormData.pickupLocation,
        pickupDate: editFormData.pickupDate,
        pickupTime: editFormData.pickupTime,
        passengers: editFormData.passengers,
        luggage: editFormData.luggage,
        flightNumber: editFormData.flightNumber,
        specialRequests: editFormData.specialRequests,
        hideCustomerPhone: !!editFormData.hideCustomerPhone,
      };

      if (editFormData.isAsDirected) {
        const h = parseInt(editFormData.hours, 10) || 0;
        if (!h) {
          toast.error('Please select number of hours');
          return;
        }
        payload.hours = String(h);
        payload.dropoffLocation = `As Directed - ${h} hours (${h * 15} miles allowance)`;
      } else {
        payload.dropoffLocation = editFormData.dropoffLocation;
        if (editDistance) payload.calculatedDistance = String(editDistance);
        if (editTime) payload.calculatedTime = String(editTime);
      }

      // Include price if admin entered/changed it
      if (editFormData.quoteAmount !== '' && editFormData.quoteAmount != null) {
        const priceNum = parseFloat(editFormData.quoteAmount);
        if (Number.isNaN(priceNum) || priceNum < 0) {
          toast.error('Please enter a valid price');
          return;
        }
        payload.quoteAmount = priceNum;
        payload.quoteNotes = editFormData.quoteNotes || '';
      }

      await axios.put(`${API}/admin/bookings/${selectedBooking.id}/edit`, payload);
      toast.success('Booking updated successfully');
      setShowEditModal(false);
      setEditDistance('');
      setEditTime('');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    }
  };

  const openCancelModal = (booking) => {
    setSelectedBooking(booking);
    setShowCancelModal(true);
  };

  const handleCancelBooking = async () => {
    try {
      await axios.post(`${API}/admin/bookings/${selectedBooking.id}/cancel`);
      toast.success('Booking cancelled successfully');
      setShowCancelModal(false);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleResendConfirmation = async (bookingId) => {
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/resend-confirmation`);
      toast.success('Booking confirmation email resent');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend confirmation');
    }
  };

  const getStatusBadge = (status) => {
    // Minimal monochrome pills — matches the rest of the brand language.
    // Status is conveyed by a small coloured dot + sharp uppercase label, not a heavy fill.
    const statusConfig = {
      'quote_requested':       { dot: 'bg-amber-500',     label: 'Quote requested' },
      'quote_sent':            { dot: 'bg-blue-500',      label: 'Quote sent' },
      'modification_pending':  { dot: 'bg-amber-500',     label: 'Modification pending' },
      'confirmed':             { dot: 'bg-emerald-600',   label: 'Confirmed' },
      'in_progress':           { dot: 'bg-violet-600',    label: 'In progress' },
      'completed':             { dot: 'bg-black',         label: 'Completed' },
      'cancelled':             { dot: 'bg-red-500',       label: 'Cancelled' },
    };
    const config = statusConfig[status] || statusConfig['quote_requested'];
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-gray-200 bg-white text-[10px] uppercase tracking-[0.18em] font-semibold text-black">
        <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
        {config.label}
      </span>
    );
  };

  // Left-edge accent colour per status — drives the hairline indicator on each card.
  const getStatusAccent = (status) => {
    return ({
      'quote_requested':       'before:bg-amber-500',
      'quote_sent':            'before:bg-blue-500',
      'modification_pending':  'before:bg-amber-500',
      'confirmed':             'before:bg-emerald-600',
      'in_progress':           'before:bg-violet-600',
      'completed':             'before:bg-black',
      'cancelled':             'before:bg-red-500',
    })[status] || 'before:bg-gray-300';
  };

  return (
    <AdminLayout
      title="Bookings"
      subtitle="Manage bookings, quotes and modifications"
      actions={
        <>
          <Button onClick={fetchBookings} variant="outline" className="border-gray-300" data-testid="refresh-bookings-btn">
            Refresh
          </Button>
        </>
      }
    >
      <div>
        {/* KPI strip — quick glance across the operation */}
        <div className="grid grid-cols-2 md:grid-cols-5 border border-gray-200 bg-white mb-6">
          {[
            { label: "TODAY'S JOBS",     value: stats.todays,        accent: 'border-l-black' },
            { label: 'PENDING QUOTES',   value: stats.pendingQuotes, accent: 'border-l-amber-500' },
            { label: 'CONFIRMED',        value: stats.confirmed,     accent: 'border-l-emerald-600' },
            { label: 'LIVE NOW',         value: stats.inProgress,    accent: 'border-l-violet-600' },
            { label: 'COMPLETED',        value: stats.completed,     accent: 'border-l-gray-700' },
          ].map((k, i) => (
            <div
              key={i}
              className={`px-5 py-4 border-l-2 ${k.accent} ${i > 0 ? 'md:border-t-0 border-t md:border-l-2' : ''} border-r border-gray-100 last:border-r-0`}
              data-testid={`bookings-kpi-${k.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <p className="text-[10px] tracking-[0.2em] font-semibold text-gray-500">{k.label}</p>
              <p className="text-3xl font-bold text-black tabular-nums mt-1">{k.value}</p>
            </div>
          ))}
        </div>

        {/* Status tabs — a smoother replacement for the dropdown */}
        <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 mb-5">
          {[
            { id: 'all',              label: 'All' },
            { id: 'quote_requested',  label: 'Requests' },
            { id: 'quote_sent',       label: 'Quoted' },
            { id: 'confirmed',        label: 'Confirmed' },
            { id: 'in_progress',      label: 'Live' },
            { id: 'completed',        label: 'Completed' },
            { id: 'cancelled',        label: 'Cancelled' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setStatusFilter(t.id)}
              data-testid={`bookings-status-tab-${t.id}`}
              className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold transition-colors border-b-2 -mb-px ${
                statusFilter === t.id
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search + driver filter */}
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
          <div className="relative flex-1 md:max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, date, address, ID, phone…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white h-10 border-gray-200"
              data-testid="bookings-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRoundTripsOnly((v) => !v)}
              className={`flex items-center gap-1.5 h-10 px-3.5 rounded-md border text-sm font-medium whitespace-nowrap transition-colors ${
                roundTripsOnly
                  ? 'border-indigo-300 bg-indigo-600 text-white'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-700'
              }`}
              data-testid="round-trips-filter"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Round Trips
            </button>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500 whitespace-nowrap">Driver</Label>
            <Select value={driverFilter} onValueChange={setDriverFilter}>
              <SelectTrigger className="w-52 bg-white h-10 border-gray-200" data-testid="bookings-driver-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {drivers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Result count when searching */}
        {searchQuery.trim() && !loading && (
          <p className="text-xs text-gray-500 mb-3" data-testid="bookings-search-count">
            {filteredBookings.length} result{filteredBookings.length === 1 ? '' : 's'} for “{searchQuery.trim()}”
          </p>
        )}

        {/* Bookings Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading bookings...</p>
          </div>
        ) : filteredBookings.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-600 text-lg">
              {searchQuery.trim() ? 'No bookings match your search' : 'No bookings found'}
            </p>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            {filteredBookings.map((booking) => (
              <Card
                key={booking.id}
                className={`relative p-6 border border-gray-200 rounded-none shadow-none hover:shadow-md transition-shadow before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:content-[''] ${getStatusAccent(booking.status)}`}
                data-testid={`booking-card-${booking.id}`}
              >
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Booking Info */}
                  <div className="flex-1 space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-xl font-bold text-black">{booking.id}</h3>
                          {getStatusBadge(booking.status)}
                          {['on_the_way', 'arrived', 'pob'].includes(booking.driverStatus) && (
                            <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full" data-testid={`live-badge-${booking.id}`}>
                              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              LIVE · {booking.driverStatus === 'on_the_way' ? 'En Route' : booking.driverStatus === 'arrived' ? 'Arrived' : 'POB'}
                            </span>
                          )}
                          {booking.reminderSent && (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800" data-testid={`reminder-sent-badge-${booking.id}`}>
                              24h Reminder Sent
                            </span>
                          )}
                          {booking.legType === 'return' && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-200" data-testid={`return-leg-badge-${booking.id}`}>
                              RETURN LEG
                            </span>
                          )}
                          {booking.returnRequired === 'yes' && booking.legType !== 'return' && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid={`return-booked-badge-${booking.id}`}>
                              RETURN BOOKED
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          Created: {new Date(booking.createdAt).toLocaleDateString('en-GB')} at {new Date(booking.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Service Type */}
                      <div className="flex items-start space-x-3">
                        <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500">Service</p>
                          <p className="font-semibold text-black">{booking.serviceType}</p>
                        </div>
                      </div>

                      {/* Vehicle */}
                      {booking.vehicleName && (
                        <div className="flex items-start space-x-3">
                          <Car className="h-5 w-5 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Vehicle</p>
                            <p className="font-semibold text-black">{booking.vehicleName}</p>
                          </div>
                        </div>
                      )}

                      {/* Customer */}
                      <div className="flex items-start space-x-3">
                        <Users className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500">Customer</p>
                          <p className="font-semibold text-black">{booking.firstName} {booking.lastName}</p>
                        </div>
                      </div>

                      {/* Pickup */}
                      <div className="flex items-start space-x-3">
                        <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500">Pickup</p>
                          <p className="font-semibold text-black text-sm">{booking.pickupLocation}</p>
                        </div>
                      </div>

                      {/* Dropoff */}
                      <div className="flex items-start space-x-3">
                        <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500">Drop-off</p>
                          <p className="font-semibold text-black text-sm">{booking.dropoffLocation}</p>
                        </div>
                      </div>

                      {/* Date & Time */}
                      <div className="flex items-start space-x-3">
                        <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500">Date & Time</p>
                          <p className="font-semibold text-black">{formatUkDateTime(booking.pickupDate, booking.pickupTime)}</p>
                        </div>
                      </div>

                      {/* Passengers & Luggage */}
                      <div className="flex items-start space-x-3">
                        <Luggage className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500">Passengers & Luggage</p>
                          <p className="font-semibold text-black">{booking.passengers} PAX | {booking.luggage} Luggage</p>
                        </div>
                      </div>

                      {/* Contact */}
                      <div className="flex items-start space-x-3">
                        <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="font-semibold text-black text-sm">{formatDisplay(booking.phone) || booking.phone}</p>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex items-start space-x-3">
                        <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="font-semibold text-black text-sm break-all">{booking.email}</p>
                        </div>
                      </div>
                    </div>

                    {/* Return Journey */}
                    {booking.returnRequired === 'yes' && booking.legType !== 'return' && (
                      <div className="mt-4 border-2 border-indigo-200 bg-indigo-50/50 p-4 rounded-lg" data-testid={`return-journey-${booking.id}`}>
                        <p className="text-xs font-bold uppercase tracking-wider text-indigo-700 mb-3">Return Journey</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Return Date &amp; Time</p>
                            <p className="font-semibold text-black">{formatUkDateTime(booking.returnDate, booking.returnTime)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Route (reversed)</p>
                            <p className="font-semibold text-black">{booking.dropoffLocation} &rarr; {booking.pickupLocation}</p>
                          </div>
                        </div>
                        {booking.linkedBookingId && (
                          <p className="text-xs text-gray-600 mt-3">Return job: <span className="font-mono font-semibold text-indigo-700">{booking.linkedBookingId}</span> (assign a driver separately)</p>
                        )}
                        {(booking.outboundFare != null || booking.returnFare != null) && (
                          <p className="text-xs text-gray-600 mt-1" data-testid={`return-fare-split-${booking.id}`}>
                            Fares — Outbound: <span className="font-semibold text-black">£{Number(booking.outboundFare || 0).toFixed(2)}</span> · Return: <span className="font-semibold text-black">£{Number(booking.returnFare || 0).toFixed(2)}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {booking.legType === 'return' && booking.linkedBookingId && (
                      <div className="mt-4 border border-indigo-200 bg-indigo-50/50 p-3 rounded-lg" data-testid={`return-leg-link-${booking.id}`}>
                        <p className="text-xs text-indigo-700">This is the return leg of outbound job: <span className="font-mono font-semibold">{booking.linkedBookingId}</span></p>
                        <div className="flex items-center gap-2 mt-2.5">
                          <span className="text-xs font-medium text-gray-700">Set Return Fare £</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            className="h-8 w-28 bg-white"
                            defaultValue={booking.legFare != null ? booking.legFare : ''}
                            onChange={(e) => setLegFareEdits((prev) => ({ ...prev, [booking.id]: e.target.value }))}
                            data-testid={`return-leg-fare-input-${booking.id}`}
                          />
                          <Button
                            size="sm"
                            className="h-8 bg-black text-white hover:bg-gray-800"
                            onClick={() => saveLegFare(booking.id, legFareEdits[booking.id] !== undefined ? legFareEdits[booking.id] : (booking.legFare ?? ''))}
                            data-testid={`return-leg-fare-save-${booking.id}`}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Distance and Time - Highlighted Box */}
                    {(booking.calculatedDistance || booking.calculatedTime) && (
                      <div className="mt-4 bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                        <div className="grid grid-cols-2 gap-4">
                          {booking.calculatedDistance && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Journey Distance</p>
                              <p className="text-xl font-bold text-gray-900">{booking.calculatedDistance} miles</p>
                            </div>
                          )}
                          {booking.calculatedTime && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Est. Travel Time</p>
                              <p className="text-xl font-bold text-gray-900">{booking.calculatedTime}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {booking.flightNumber && (
                      <div className="pt-4 border-t">
                        <p className="text-xs text-gray-500 mb-1">Flight</p>
                        <a
                          href={`https://www.flightradar24.com/data/flights/${String(booking.flightNumber).replace(/[^A-Za-z0-9]/g, '').toLowerCase()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 font-semibold text-black hover:underline"
                          data-testid={`flightradar-${booking.id}`}
                        >
                          <Plane className="h-4 w-4" />
                          {String(booking.flightNumber).toUpperCase()}
                          <ExternalLink className="h-3 w-3 text-gray-400" />
                        </a>
                      </div>
                    )}

                    {booking.bookingForOther && (booking.passengerName || booking.passengerPhone) && (
                      <div className="pt-4 border-t" data-testid={`passenger-info-${booking.id}`}>
                        <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider font-semibold">Passenger (not booker)</p>
                        <p className="text-sm font-medium text-black">{booking.passengerName || '—'}</p>
                        {booking.passengerPhone && (
                          <p className="text-xs text-gray-700">{formatDisplay(booking.passengerPhone) || booking.passengerPhone}</p>
                        )}
                        {booking.passengerEmail && (
                          <p className="text-xs text-gray-700">{booking.passengerEmail}</p>
                        )}
                      </div>
                    )}

                    {booking.isCorporate && (booking.companyName || booking.costReference) && (
                      <div className="pt-4 border-t" data-testid={`corporate-info-${booking.id}`}>
                        <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider font-semibold">Corporate Booking</p>
                        <p className="text-sm font-medium text-black">{booking.companyName || '—'}</p>
                        {booking.costReference && (
                          <p className="text-xs text-gray-700">Ref: {booking.costReference}</p>
                        )}
                        {booking.billingEmail && (
                          <p className="text-xs text-gray-700">Bill to: {booking.billingEmail}</p>
                        )}
                      </div>
                    )}

                    {booking.specialRequests && (
                      <div className="pt-4 border-t">
                        <p className="text-xs text-gray-500 mb-1">Special Requests</p>
                        <p className="text-sm text-gray-700">{booking.specialRequests}</p>
                      </div>
                    )}

                    {/* Quote Amount */}
                    {booking.quoteAmount && (
                      <div className="pt-4 border-t">
                        <p className="text-xs text-gray-500 mb-1">Quote Amount</p>
                        <p className="text-lg font-bold text-black">£{Number(booking.quoteAmount).toFixed(2)}</p>
                      </div>
                    )}

                    {/* Quote History timeline */}
                    {Array.isArray(booking.quoteHistory) && booking.quoteHistory.length > 0 && (
                      <div className="pt-4 border-t" data-testid={`quote-history-${booking.id}`}>
                        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-semibold">Quote History</p>
                        <div className="space-y-1.5">
                          {booking.quoteHistory.slice().reverse().map((h, i) => {
                            const fromAmt = h.previousAmount;
                            const toAmt = h.amount;
                            const delta = (fromAmt != null) ? (toAmt - fromAmt) : null;
                            const issuedAt = h.issuedAt ? new Date(h.issuedAt) : null;
                            return (
                              <div key={i} className="flex items-center gap-3 text-sm">
                                <div className="flex items-center gap-1.5 font-medium text-black">
                                  {fromAmt != null && (
                                    <>
                                      <span className="text-gray-500 line-through">£{Number(fromAmt).toFixed(2)}</span>
                                      <span className="text-gray-300">→</span>
                                    </>
                                  )}
                                  <span>£{Number(toAmt).toFixed(2)}</span>
                                  {delta != null && delta !== 0 && (
                                    <span className={`text-xs font-semibold ${delta > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                                      {delta > 0 ? '+' : ''}£{Math.abs(delta).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                                {Array.isArray(h.modificationChanges) && h.modificationChanges.length > 0 && (
                                  <span className="text-xs text-gray-500 truncate flex-1">
                                    · {h.modificationChanges.join(', ')}
                                  </span>
                                )}
                                {issuedAt && (
                                  <span className="text-[11px] text-gray-400 shrink-0">
                                    {issuedAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Modification Pending Banner */}
                    {booking.status === 'modification_pending' && (
                      <div className="mt-4 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg" data-testid={`modification-banner-${booking.id}`}>
                        <p className="text-sm font-bold text-amber-900 mb-1">Customer Modification Pending Approval</p>
                        {booking.modificationChanges && booking.modificationChanges.length > 0 && (
                          <p className="text-sm text-amber-800">
                            Changed: <span className="font-semibold">{booking.modificationChanges.join(', ')}</span>
                          </p>
                        )}
                        {booking.modificationRequestedAt && (
                          <p className="text-xs text-amber-700 mt-1">
                            Requested: {new Date(booking.modificationRequestedAt).toLocaleString('en-GB')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 lg:w-52">
                    {booking.status === 'quote_requested' && (
                      <Button
                        onClick={() => openQuoteModal(booking)}
                        className="bg-black text-white hover:bg-gray-800 rounded-none w-full text-xs uppercase tracking-[0.15em] font-semibold h-10"
                        data-testid={`send-quote-btn-${booking.id}`}
                      >
                        <Send className="h-3.5 w-3.5 mr-2" />
                        Send Quote
                      </Button>
                    )}

                    {booking.status === 'modification_pending' && (
                      <Button
                        onClick={() => openQuoteModal(booking)}
                        className="bg-amber-500 text-white hover:bg-amber-600 rounded-none w-full text-xs uppercase tracking-[0.15em] font-semibold h-10"
                        data-testid={`approve-modification-btn-${booking.id}`}
                      >
                        <Send className="h-3.5 w-3.5 mr-2" />
                        Approve & Re-quote
                      </Button>
                    )}

                    {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                      <>
                        <Button
                          onClick={() => openEditModal(booking)}
                          variant="outline"
                          className="border border-gray-300 rounded-none w-full text-xs uppercase tracking-[0.15em] font-semibold h-10 hover:bg-black hover:text-white hover:border-black"
                          data-testid={`edit-booking-btn-${booking.id}`}
                        >
                          <Edit className="h-3.5 w-3.5 mr-2" />
                          Edit Booking
                        </Button>

                        <Button
                          onClick={() => openCancelModal(booking)}
                          variant="outline"
                          className="border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-none w-full text-xs uppercase tracking-[0.15em] font-semibold h-10"
                          data-testid={`cancel-booking-btn-${booking.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-2" />
                          Cancel
                        </Button>
                      </>
                    )}

                    {(booking.status === 'confirmed' || booking.status === 'in_progress') && (
                      <a href={`/driver/${booking.id}`} target="_blank" rel="noopener noreferrer">
                        <Button
                          variant="outline"
                          className="border border-gray-300 rounded-none w-full text-xs uppercase tracking-[0.15em] font-semibold h-10 hover:bg-black hover:text-white hover:border-black"
                          data-testid={`driver-portal-link-${booking.id}`}
                        >
                          Driver Portal
                        </Button>
                      </a>
                    )}

                    {booking.status === 'confirmed' && (
                      <Button
                        onClick={() => handleResendConfirmation(booking.id)}
                        variant="outline"
                        className="border border-gray-300 rounded-none w-full text-xs uppercase tracking-[0.15em] font-semibold h-10 hover:bg-black hover:text-white hover:border-black"
                        data-testid={`resend-confirmation-btn-${booking.id}`}
                      >
                        <Mail className="h-3.5 w-3.5 mr-2" />
                        Resend
                      </Button>
                    )}

                    {/* Job Log — ALWAYS visible on every booking so admins can retrace any job
                        (especially completed ones) without opening edit modal. */}
                    <Button
                      onClick={() => { setSelectedBooking(booking); setShowJobLog(true); }}
                      variant="outline"
                      className="border border-gray-300 rounded-none w-full text-xs uppercase tracking-[0.15em] font-semibold h-10 hover:bg-black hover:text-white hover:border-black"
                      data-testid={`job-log-btn-${booking.id}`}
                    >
                      <FileSearch className="h-3.5 w-3.5 mr-2" />
                      Job Log
                    </Button>

                    {['confirmed', 'in_progress', 'quote_sent'].includes(booking.status) && buildGoogleCalendarUrl(booking) && (
                      <a href={buildGoogleCalendarUrl(booking)} target="_blank" rel="noopener noreferrer">
                        <Button
                          variant="outline"
                          className="border border-gray-300 rounded-none w-full text-xs uppercase tracking-[0.15em] font-semibold h-10 hover:bg-black hover:text-white hover:border-black"
                          data-testid={`add-to-calendar-btn-${booking.id}`}
                        >
                          <CalendarPlus className="h-3.5 w-3.5 mr-2" />
                          Add to Calendar
                        </Button>
                      </a>
                    )}

                    {['confirmed', 'in_progress', 'completed'].includes(booking.status) && (
                      <Button
                        onClick={() => navigate(`/admin/invoices?booking=${booking.id}`)}
                        className="bg-black text-white rounded-none w-full text-xs uppercase tracking-[0.15em] font-semibold h-10 hover:bg-gray-800"
                        data-testid={`generate-invoice-btn-${booking.id}`}
                      >
                        <FileText className="h-3.5 w-3.5 mr-2" />
                        Generate Invoice
                        {booking.tripExtras && <span className="ml-2 px-1.5 py-0.5 bg-amber-400 text-black rounded text-[9px]">extras</span>}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Quote Modal */}
      {showQuoteModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            {selectedBooking.status === 'modification_pending' ? (
              <>
                <h2 className="text-2xl font-bold mb-2 text-black">Approve & Re-quote</h2>
                <p className="text-sm text-gray-600 mb-6">
                  Confirm the modification by issuing a fresh quote. The customer will receive an updated quote email and must re-accept before the booking returns to confirmed.
                </p>
                {selectedBooking.modificationChanges?.length > 0 && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs font-semibold text-amber-900 mb-1">Customer changed:</p>
                    <p className="text-sm text-amber-800">{selectedBooking.modificationChanges.join(', ')}</p>
                  </div>
                )}
              </>
            ) : (
              <h2 className="text-2xl font-bold mb-6 text-black">Send Quote</h2>
            )}

            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Booking: <span className="font-bold text-black">{selectedBooking.id}</span></p>
              <p className="text-sm text-gray-600 mb-2">Customer: <span className="font-semibold text-black">{selectedBooking.firstName} {selectedBooking.lastName}</span></p>
              <p className="text-sm text-gray-600">Service: <span className="font-semibold text-black">{selectedBooking.serviceType}</span></p>
              {selectedBooking.status === 'modification_pending' && selectedBooking.quoteAmount && (
                <p className="text-sm text-gray-600 mt-2">Previous amount: <span className="font-semibold text-black">£{Number(selectedBooking.quoteAmount).toFixed(2)}</span></p>
              )}
            </div>

            <div className="space-y-4 mb-6">
              {selectedBooking.returnRequired === 'yes' ? (
                <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-4" data-testid="quote-return-fares">
                  <p className="text-xs font-bold uppercase tracking-wider text-indigo-700 mb-3">Price both journeys</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-900 mb-2 font-medium">Outbound Fare (£) *</Label>
                      <Input
                        type="number"
                        value={outboundFare}
                        onChange={(e) => setOutboundFare(e.target.value)}
                        placeholder="250.00"
                        className="bg-white border-gray-300"
                        step="0.01"
                        min="0"
                        data-testid="quote-outbound-fare"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-900 mb-2 font-medium">Return Fare (£) *</Label>
                      <Input
                        type="number"
                        value={returnFare}
                        onChange={(e) => setReturnFare(e.target.value)}
                        placeholder="250.00"
                        className="bg-white border-gray-300"
                        step="0.01"
                        min="0"
                        data-testid="quote-return-fare"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-indigo-200">
                    <span className="text-sm font-medium text-gray-700">Total quoted to customer</span>
                    <span className="text-xl font-bold text-black" data-testid="quote-total">
                      £{(((parseFloat(outboundFare) || 0) + (parseFloat(returnFare) || 0))).toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">The customer sees one combined total. Per-leg fares are used only for invoicing.</p>
                </div>
              ) : (
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Quote Amount (£) *</Label>
                  <Input
                    type="number"
                    value={quoteAmount}
                    onChange={(e) => setQuoteAmount(e.target.value)}
                    placeholder="250.00"
                    className="bg-white border-gray-300"
                    step="0.01"
                    min="0"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Distance (miles)</Label>
                  <Input
                    type="number"
                    value={quoteDistance}
                    onChange={(e) => setQuoteDistance(e.target.value)}
                    placeholder="125"
                    className="bg-white border-gray-300"
                    step="0.1"
                    min="0"
                  />
                </div>

                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Est. Journey Time</Label>
                  <Input
                    value={quoteEstimatedTime}
                    onChange={(e) => setQuoteEstimatedTime(e.target.value)}
                    placeholder="2 hours 30 minutes"
                    className="bg-white border-gray-300"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-900 mb-2 font-medium">Additional Notes (Optional)</Label>
                <Textarea
                  value={quoteNotes}
                  onChange={(e) => setQuoteNotes(e.target.value)}
                  placeholder="Any additional information for the customer..."
                  rows={4}
                  className="bg-white border-gray-300 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={() => {
                  setShowQuoteModal(false);
                  setQuoteAmount('');
                  setQuoteNotes('');
                  setQuoteDistance('');
                  setQuoteEstimatedTime('');
                  setOutboundFare('');
                  setReturnFare('');
                  setSelectedBooking(null);
                }}
                variant="outline"
                className="flex-1 border-gray-300"
                disabled={sendingQuote}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendQuote}
                className="flex-1 bg-black text-white hover:bg-gray-800"
                disabled={sendingQuote}
              >
                {sendingQuote
                  ? 'Sending...'
                  : selectedBooking?.status === 'modification_pending'
                    ? 'Send Updated Quote'
                    : 'Send Quote'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Edit Booking Modal */}
      {showEditModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-booking-modal">
          <Card className="max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-black">Edit Booking</h2>

            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Booking: <span className="font-bold text-black">{selectedBooking.id}</span></p>
              <p className="text-sm text-gray-600">Customer: <span className="font-semibold text-black">{selectedBooking.firstName} {selectedBooking.lastName}</span></p>
              <p className="text-sm text-gray-600 mt-1">Service: <span className="font-semibold text-black">{selectedBooking.serviceType}</span></p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <Label className="text-gray-900 mb-2 font-medium">
                  {editFormData.isAsDirected ? 'Starting Location' : 'Pickup Location'}
                </Label>
                {gmapsLoaded ? (
                  <Autocomplete
                    onLoad={(ac) => setEditPickupAutocomplete(ac)}
                    onPlaceChanged={onEditPickupPlaceChanged}
                    options={{ componentRestrictions: { country: 'gb' } }}
                  >
                    <Input
                      value={editFormData.pickupLocation || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, pickupLocation: e.target.value })}
                      placeholder="Start typing an address..."
                      className="bg-white border-gray-300"
                      data-testid="edit-pickup-location"
                    />
                  </Autocomplete>
                ) : (
                  <Input
                    value={editFormData.pickupLocation || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, pickupLocation: e.target.value })}
                    className="bg-white border-gray-300"
                    data-testid="edit-pickup-location"
                  />
                )}
              </div>

              {editFormData.isAsDirected ? (
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Number of Hours (15 miles per hour allowance)</Label>
                  <Select
                    value={editFormData.hours || ''}
                    onValueChange={(value) => setEditFormData({ ...editFormData, hours: value })}
                  >
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="edit-hours-select">
                      <SelectValue placeholder="Select hours" />
                    </SelectTrigger>
                    <SelectContent>
                      {AS_DIRECTED_HOURS.map((h) => (
                        <SelectItem key={h} value={String(h)}>
                          {h} hours ({h * 15} miles allowance)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-2">
                    Each hour includes a 15-mile allowance. Additional mileage charged separately.
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">Drop-off Location</Label>
                    {gmapsLoaded ? (
                      <Autocomplete
                        onLoad={(ac) => setEditDropoffAutocomplete(ac)}
                        onPlaceChanged={onEditDropoffPlaceChanged}
                        options={{ componentRestrictions: { country: 'gb' } }}
                      >
                        <Input
                          value={editFormData.dropoffLocation || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, dropoffLocation: e.target.value })}
                          placeholder="Start typing an address..."
                          className="bg-white border-gray-300"
                          data-testid="edit-dropoff-location"
                        />
                      </Autocomplete>
                    ) : (
                      <Input
                        value={editFormData.dropoffLocation || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, dropoffLocation: e.target.value })}
                        className="bg-white border-gray-300"
                        data-testid="edit-dropoff-location"
                      />
                    )}
                  </div>

                  {(editDistance || editTime || editCalculating) && (
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 p-4 rounded-r-lg" data-testid="edit-distance-time-card">
                      {editCalculating ? (
                        <p className="text-sm text-gray-700">Calculating distance and travel time…</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-4">
                          {editDistance && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Distance</p>
                              <p className="text-lg font-bold text-gray-900" data-testid="edit-distance-value">{editDistance} miles</p>
                            </div>
                          )}
                          {editTime && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Est. Travel Time</p>
                              <p className="text-lg font-bold text-gray-900" data-testid="edit-time-value">{editTime}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Pickup Date</Label>
                  <Input
                    type="date"
                    value={editFormData.pickupDate || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, pickupDate: e.target.value })}
                    className="bg-white border-gray-300"
                    data-testid="edit-pickup-date"
                  />
                </div>
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Pickup Time</Label>
                  <Input
                    type="time"
                    value={editFormData.pickupTime || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, pickupTime: e.target.value })}
                    className="bg-white border-gray-300"
                    data-testid="edit-pickup-time"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Passengers</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editFormData.passengers || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, passengers: e.target.value })}
                    className="bg-white border-gray-300"
                    data-testid="edit-passengers"
                  />
                </div>
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Luggage</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editFormData.luggage || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, luggage: e.target.value })}
                    className="bg-white border-gray-300"
                    data-testid="edit-luggage"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-900 mb-2 font-medium">Flight Number (optional)</Label>
                <Input
                  value={editFormData.flightNumber || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, flightNumber: e.target.value })}
                  className="bg-white border-gray-300"
                  data-testid="edit-flight-number"
                />
              </div>

              <div>
                <Label className="text-gray-900 mb-2 font-medium">Special Requests</Label>
                <Textarea
                  value={editFormData.specialRequests || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, specialRequests: e.target.value })}
                  rows={3}
                  className="bg-white border-gray-300 resize-none"
                  data-testid="edit-special-requests"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700 select-none" data-testid="edit-hide-phone-toggle">
                  <input
                    type="checkbox"
                    checked={!!editFormData.hideCustomerPhone}
                    onChange={(e) => setEditFormData({ ...editFormData, hideCustomerPhone: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Hide customer phone number from the chauffeur
                </label>
              </div>

              {/* Price section in same modal */}
              <div className="pt-4 border-t">
                <p className="text-sm font-semibold text-black mb-3">Price (optional — leave unchanged to keep current)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">Quote Amount (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editFormData.quoteAmount || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, quoteAmount: e.target.value })}
                      placeholder="250.00"
                      className="bg-white border-gray-300"
                      data-testid="edit-quote-amount"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">Price Notes</Label>
                    <Input
                      value={editFormData.quoteNotes || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, quoteNotes: e.target.value })}
                      placeholder="Reason / breakdown"
                      className="bg-white border-gray-300"
                      data-testid="edit-quote-notes"
                    />
                  </div>
                </div>
              </div>

              {/* Job log — opens in a popup */}
              <div className="pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowJobLog(true)}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-black border border-gray-300 px-3 py-2 hover:bg-black hover:text-white transition-colors"
                  data-testid="open-job-log-btn"
                >
                  <FileSearch className="h-3.5 w-3.5" />
                  Job Log
                </button>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={() => { setShowEditModal(false); setSelectedBooking(null); setEditDistance(''); setEditTime(''); }}
                variant="outline"
                className="flex-1 border-gray-300"
                data-testid="edit-modal-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditBooking}
                className="flex-1 bg-black text-white hover:bg-gray-800"
                data-testid="edit-modal-save-btn"
              >
                Save Changes
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Job Log Popup — full audit timeline in a sleek black-header modal */}
      {showJobLog && selectedBooking && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
          data-testid="job-log-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowJobLog(false); }}
        >
          <div className="bg-white max-w-2xl w-full max-h-[88vh] overflow-hidden flex flex-col">
            {/* Sleek black header */}
            <div className="bg-black text-white px-7 py-5 flex items-start justify-between">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-white/60">Audit Trail</p>
                <h2 className="text-2xl font-bold mt-1 tracking-tight">Job Log</h2>
                <p className="text-xs text-white/60 mt-1 font-light">
                  {selectedBooking.id} · {selectedBooking.firstName} {selectedBooking.lastName}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`/admin/bookings/${selectedBooking.id}/log`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] uppercase tracking-[0.18em] text-white/70 hover:text-white border border-white/20 px-3 py-1.5 transition-colors"
                  data-testid="job-log-open-page"
                >
                  Open page →
                </a>
                <button
                  type="button"
                  onClick={() => setShowJobLog(false)}
                  className="text-white/70 hover:text-white p-1.5"
                  data-testid="job-log-close"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            {/* Scrollable timeline */}
            <div className="overflow-y-auto px-7 py-6">
              <JobLogTimeline bookingId={selectedBooking.id} />
            </div>
          </div>
        </div>
      )}



      {/* Cancel Booking Modal */}
      {showCancelModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="cancel-booking-modal">
          <Card className="max-w-md w-full p-8">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="h-8 w-8 text-red-600" />
              <h2 className="text-2xl font-bold text-black">Cancel Booking</h2>
            </div>

            <p className="text-gray-700 mb-2">
              Are you sure you want to cancel booking <span className="font-bold text-black">{selectedBooking.id}</span>?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Customer: {selectedBooking.firstName} {selectedBooking.lastName} — {selectedBooking.pickupDate} at {selectedBooking.pickupTime}
            </p>
            <p className="text-sm text-red-700 mb-6 bg-red-50 p-3 rounded-lg border border-red-200">
              This action cannot be undone. The booking will be marked as cancelled.
            </p>

            <div className="flex gap-4">
              <Button
                onClick={() => { setShowCancelModal(false); setSelectedBooking(null); }}
                variant="outline"
                className="flex-1 border-gray-300"
                data-testid="cancel-modal-back-btn"
              >
                Keep Booking
              </Button>
              <Button
                onClick={handleCancelBooking}
                className="flex-1 bg-red-600 text-white hover:bg-red-700"
                data-testid="cancel-modal-confirm-btn"
              >
                Yes, Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
